/**
 * English strings of the account screens (milestone 5) and the legal pages. Same rule as en.ts:
 * `account-it.ts` is typed against this shape, so a missing or extra key is a compile error.
 *
 * The legal texts are DRAFTS written for the developer to hand to a lawyer; they are not legal
 * advice and have not been reviewed (docs/STATUS.md). The pages say so.
 */
export const accountEn = {
  account: {
    title: 'Account',
    loading: 'Connecting to the account service',
    unavailable: {
      title: 'Accounts are not available in this version',
      body: 'The game is complete without one: play solo, train and follow the tutorial. An account adds a profile, cloud save and, soon, online play.',
    },
    loadFailed: {
      title: 'The account service did not answer',
      body: 'Check your connection and try again. Your game is not affected.',
      retry: 'Try again',
    },
    demo: {
      banner:
        'Development build: accounts run on a stand-in backend inside this page. No e-mail is sent and nothing leaves your device.',
      mailTitle: 'Test mailbox',
      mailEmpty: 'No mail yet.',
      mailLast: 'Last code for {{to}}',
    },
    auth: {
      tabs: { signIn: 'Sign in', signUp: 'Create account' },
      email: 'E-mail',
      password: 'Password',
      passwordHint: 'At least 10 characters.',
      newPassword: 'New password',
      code: 'Six-digit code',
      signIn: 'Sign in',
      signUp: 'Create account',
      forgot: 'Forgot your password?',
      useCode: 'Use a one-time code instead',
      usePassword: 'Use a password instead',
      sendCode: 'Send me a code',
      codeTitle: 'Enter the code',
      codeSent: 'We sent a six-digit code to {{email}}. It is valid for one hour.',
      confirmTitle: 'Confirm your e-mail',
      verify: 'Confirm',
      resend: 'Send a new code',
      resent: 'A new code is on its way.',
      resetTitle: 'Reset your password',
      resetBody: 'We will send a code to your e-mail. With it you can choose a new password.',
      resetSend: 'Send the code',
      setPassword: 'Save the new password',
      passwordSaved: 'Password saved.',
      providers: 'Or continue with',
      provider: { google: 'Google', discord: 'Discord', apple: 'Apple' },
      guestTitle: 'Just want to look around?',
      guestBody:
        'Continue as a guest. You can add an e-mail or a provider later and keep everything.',
      guest: 'Continue as a guest',
      guestBodyOnly: 'Continue as a guest. Your profile and your progress stay on this device.',
      guestOnly:
        'For now you get in as a guest. Signing in with an e-mail or with Google is not switched on yet.',
      providersOnly: 'Sign in with an account you already have, or carry on as a guest.',
      confirmUnavailable:
        'Accounts with an e-mail cannot be confirmed yet. Continue as a guest for now.',
      terms:
        'You will be asked to accept the Terms of use and the Privacy notice before anything is saved online.',
      back: 'Back',
      secondFactorTitle: 'Second step',
      secondFactorBody: 'Enter the six-digit code shown by your authenticator app.',
      secondFactorVerify: 'Verify',
      secondFactorOut: 'Sign out',
    },
    overview: {
      guest: 'Guest',
      guestBannerTitle: 'You are playing as a guest',
      guestBannerBody:
        'Add an e-mail or a provider to keep your profile if you change device or clear this one.',
      guestBannerBodyOnly:
        'Your profile lives on this device. E-mail and provider accounts are not switched on yet.',
      guestBannerAction: 'Secure my account',
      signedInWith: 'Signed in with {{methods}}',
      methodEmail: 'e-mail',
      links: {
        profile: 'Profile',
        profileHint: 'Nickname and avatar',
        progress: 'Rap sheet and challenges',
        progressHint: 'Level, ratings, trophies, what you have earned',
        security: 'Sign-in and security',
        securityHint: 'E-mail, providers, authenticator app',
        privacy: 'Privacy and data',
        privacyHint: 'Consents, export, deletion',
        admin: 'Team area',
        adminHint: 'Reports, players, live settings',
      },
      signOut: 'Sign out',
      signOutTitle: 'Sign out?',
      signOutBody: 'Your game keeps working. Choose how far to sign out.',
      signOutHere: 'This device only',
      signOutEverywhere: 'Every device',
      signOutWipe: 'Also remove my progress from this device',
      signOutWipeHint: 'Use this on a device that is not yours. Your cloud save is not touched.',
    },
    consent: {
      title: 'Before you continue',
      body: 'To keep your profile and your save online, and later to play with others, we need you to read and accept two documents.',
      terms: 'I accept the Terms of use',
      privacy: 'I have read the Privacy notice',
      read: 'Read',
      accept: 'Accept',
      draft: 'Draft texts, pending legal review.',
    },
    age: {
      title: 'How old are you?',
      body: 'We use this only to decide which kind of chat you can use. It is not shown to anyone.',
      under14: 'Under 14',
      from14: '14 to 17',
      adult: '18 or older',
      confirm: 'Confirm',
      current: 'Age group: {{group}}',
      locked: 'You can correct it to a younger group. To move to an older one, contact support.',
      notDeclared: 'Not declared yet: only quick phrases are available in chat.',
      chat: {
        phrases: 'Chat: quick phrases only',
        free: 'Chat: free text and quick phrases',
      },
    },
    sync: {
      title: 'Cloud save',
      off: 'Cloud save needs an account with an e-mail or a provider.',
      needsConsent: 'Waiting for you to accept the documents above.',
      idle: 'Ready.',
      syncing: 'Syncing…',
      ok: 'Up to date, {{time}}.',
      okNever: 'Up to date.',
      error: 'Could not sync. Your game is safe on this device; we will try again.',
      now: 'Sync now',
      what: 'Settings (not graphics) and progress follow you from device to device.',
    },
    deletion: {
      pending: 'Your account will be deleted on {{date}}.',
      cancel: 'Keep my account',
      cancelled: 'Deletion cancelled.',
    },
    profile: {
      title: 'Profile',
      nickname: 'Nickname',
      nicknameHint: 'Letters and numbers. A number is added to it: {{example}}.',
      handle: 'Your handle: {{handle}}',
      save: 'Save',
      saved: 'Saved.',
      cooldown: 'You can change your nickname once a week. Next change: {{date}}.',
      avatarTitle: 'Avatar',
      avatarRandom: 'Surprise me',
      parts: { hat: 'Hat', mask: 'Mask', coat: 'Coat', rim: 'Light', suit: 'Emblem' },
      hat: {
        fedora: 'Fedora',
        cap: 'Cap',
        beret: 'Beret',
        bowler: 'Bowler',
        headphones: 'Headphones',
        none: 'None',
        tophat: 'Top hat',
        panama: 'Panama hat',
        hood: 'Hood',
      },
      mask: {
        none: 'None',
        domino: 'Domino',
        bandana: 'Bandana',
        visor: 'Visor',
        fox: 'Fox',
        skull: 'Skull',
      },
      coat: {
        ink: 'Ink',
        navy: 'Navy',
        plum: 'Plum',
        olive: 'Olive',
        brown: 'Brown',
        grey: 'Grey',
        crimson: 'Crimson',
        ivory: 'Ivory',
      },
      rim: {
        cyan: 'Cyan',
        amber: 'Amber',
        green: 'Green',
        pink: 'Pink',
        chalk: 'Chalk',
        violet: 'Violet',
        gold: 'Gold',
        ghost: 'Ghost',
      },
      suit: {
        spade: 'Spade',
        heart: 'Heart',
        diamond: 'Diamond',
        club: 'Club',
        star: 'Star',
        crown: 'Crown',
      },
      moreToUnlock: 'More outfits are unlocked by playing.',
      toCollection: 'See the collection',
      nicknameProblem: {
        tooShort: 'Too short.',
        tooLong: 'Too long.',
        characters: 'Use letters, numbers, spaces, dots, dashes and underscores only.',
        noLetter: 'Include at least one letter.',
        blocked: 'This name is not allowed.',
        reserved: 'This name is reserved.',
      },
      avatarLabel: 'Avatar of {{name}}',
    },
    security: {
      title: 'Sign-in and security',
      methods: 'Sign-in methods',
      methodGuest: 'Guest session (no e-mail, no provider)',
      methodEmail: 'E-mail: {{email}}',
      methodProvider: 'Provider: {{provider}}',
      addEmailTitle: 'Add an e-mail and a password',
      addEmailBody: 'Your profile and progress stay exactly as they are.',
      addEmailSend: 'Send the confirmation code',
      addEmailDone: 'E-mail added. You are now signed in with it.',
      linkTitle: 'Link another way to sign in',
      linkProvider: 'Link {{provider}}',
      linkHint: 'You will be taken to the provider and brought back here.',
      passwordTitle: 'Change password',
      authenticatorTitle: 'Authenticator app',
      authenticatorOff: 'Add a second step at sign-in: a six-digit code from an app on your phone.',
      authenticatorOn: 'Authenticator app: on.',
      authenticatorSetup: 'Set up',
      authenticatorScan:
        'Scan this picture with your authenticator app, or type the key into it. Then enter the code it shows.',
      authenticatorKey: 'Key',
      authenticatorConfirm: 'Turn on',
      authenticatorRemove: 'Turn off',
      authenticatorEnabled: 'Authenticator app turned on.',
      authenticatorRemoved: 'Authenticator app turned off.',
      authenticatorQrAlt: 'QR code for your authenticator app',
      authenticatorQrNote: 'In the development build this picture is a placeholder: use the key.',
      signOutEverywhere: 'Sign out of every device',
    },
    privacy: {
      title: 'Privacy and data',
      consentsTitle: 'Your consents',
      termsLabel: 'Terms of use',
      privacyLabel: 'Privacy notice',
      analyticsLabel: 'Anonymous usage statistics',
      analyticsBody: 'Helps us find what is too hard or broken. Off unless you turn it on.',
      granted: 'Accepted, version {{version}}',
      notGranted: 'Not accepted',
      outdated: 'A newer version needs your attention.',
      withdrawNote:
        'The Terms and the Privacy notice are needed to keep an account. To withdraw them, delete the account.',
      exportTitle: 'Your data',
      exportBody: 'Download everything we keep about you as a file.',
      exportAction: 'Download my data',
      exportDone: 'File ready.',
      deleteTitle: 'Delete my account',
      deleteBody:
        'Your profile, consents and cloud save are erased. Your progress stays on this device unless you remove it.',
      deleteAction: 'Delete my account…',
      deleteDialogTitle: 'Delete your account?',
      deleteDialogBody:
        'You can take it back for seven days, or erase everything right now. Erasing is final.',
      deleteLater: 'Delete in 7 days',
      deleteNow: 'Delete right now',
      deleteScheduled: 'Deletion scheduled for {{date}}.',
      deleteDone: 'Account deleted.',
    },
    callback: {
      title: 'Signing you in',
      body: 'One moment…',
      failed: 'We could not complete the sign-in.',
      home: 'Back to the account',
    },
    errors: {
      invalidCredentials: 'Wrong e-mail or password.',
      emailNotConfirmed: 'Confirm your e-mail first: use the code we sent you.',
      userExists: 'This e-mail or provider is already in use. Try signing in.',
      weakPassword: 'This password is too weak. Use at least 10 characters.',
      invalidEmail: 'This e-mail does not look right.',
      rateLimited: 'Too many tries. Wait a moment and try again.',
      captchaFailed: 'The check against robots failed. Try again.',
      codeInvalid: 'This code is wrong or has expired.',
      secondFactorInvalid: 'This code is wrong or has expired.',
      providerDisabled: 'This sign-in method is not available yet.',
      signupDisabled: 'New accounts cannot be created right now.',
      notSignedIn: 'Your session has ended. Sign in again.',
      notConfigured: 'Accounts are not available in this version.',
      network: 'No connection to the account service. Check your network.',
      conflict: 'Your data changed somewhere else. Try again.',
      nicknameCooldown: 'You changed your nickname recently. Wait a week.',
      nicknameTaken: 'This nickname is too popular. Try another.',
      ageLocked: 'This age group can only be changed by support.',
      invalid: 'Something in what you entered is not valid.',
      tooLarge: 'This is too big to save.',
      forbidden: 'You are not allowed to do this.',
      notFound: 'We could not find it.',
      unknown: 'Something went wrong. Try again.',
    },
  },
  legal: {
    title: 'Legal',
    draftBanner:
      'Draft. This text has not been reviewed by a lawyer yet and may change before the public release.',
    updated: 'Version {{version}}',
    links: {
      terms: 'Terms of use',
      privacy: 'Privacy notice',
      deleteAccount: 'Delete your account',
    },
    terms: {
      title: 'Terms of use',
      sections: {
        s1: {
          heading: 'Who we are and what this is',
          body: 'Il Colpo is a cooperative heist game for three to five players, played online or against bots. These terms apply to the game, its website and the apps. [Publisher name, address and contact: to be filled in by the developer.]',
        },
        s2: {
          heading: 'Playing without an account',
          body: 'You can play solo, train and follow the tutorial without any account. Nothing in this section needs the terms below.',
        },
        s3: {
          heading: 'Your account',
          body: 'An account lets you keep a profile, save your settings and progress online and play with others. Keep your sign-in details to yourself. You are responsible for what happens under your account. You can delete it at any time from the Privacy and data page.',
        },
        s4: {
          heading: 'How we expect you to behave',
          body: 'Play fair and be kind. Do not cheat, harass others, use offensive nicknames, or try to break the game or its servers. In chat you may only use the options your age group allows. We may remove content, restrict features or close accounts that break these rules.',
        },
        s5: {
          heading: 'Children',
          body: 'Players under 14 cannot use free-text chat. [The minimum age for an account and whether a parent must consent depend on the country: to be decided with a lawyer before release.]',
        },
        s6: {
          heading: 'Purchases',
          body: 'There are no purchases in this version. If they are added, these terms will say what you buy and how refunds work before you pay.',
        },
        s7: {
          heading: 'Changes and ending',
          body: 'We may update these terms. When a change is important we will ask you to accept the new version. You can stop playing and delete your account whenever you like. We may suspend the service for maintenance.',
        },
        s8: {
          heading: 'Liability and law',
          body: 'The game is provided as it is. [Limits of liability, governing law and venue: to be written with a lawyer.]',
        },
      },
    },
    privacy: {
      title: 'Privacy notice',
      sections: {
        s1: {
          heading: 'Who is responsible',
          body: 'The data controller is [name, address and contact of the publisher: to be filled in by the developer]. You can write to us to exercise your rights.',
        },
        s2: {
          heading: 'What we keep, and why',
          body: 'Without an account, the game keeps your settings and progress on your device only. With an account we keep: your e-mail or the provider identity you used to sign in, your nickname, avatar and chosen language, the age group you declared, the answers you gave to consents, and a copy of your settings and progress. We use them to run your account, to save your game across devices and, from the online release, to match you with other players.',
        },
        s3: {
          heading: 'Legal basis',
          body: 'Running your account and your cloud save is needed to provide the service you asked for. Anonymous usage statistics are collected only if you turn them on, and you can turn them off at any time. [Legal bases to be confirmed with a lawyer.]',
        },
        s4: {
          heading: 'Who handles your data',
          body: 'We use Supabase to host accounts and data, and Cloudflare Turnstile to tell people from robots. If you sign in with Google, Discord or Apple, that provider tells us who you are according to its own rules. [Processor list, regions and transfers outside the EU: to be confirmed.]',
        },
        s5: {
          heading: 'How long we keep it',
          body: 'We keep your data while your account exists. When you delete your account we erase your profile, consents and saved game; if you chose the seven-day delay, the erasure happens at the end of it unless you cancel. [Retention of backups and logs: to be confirmed.]',
        },
        s6: {
          heading: 'Your rights',
          body: 'You can see and download your data, correct your nickname and avatar, withdraw optional consents and delete your account from the Privacy and data page. You may also contact us, and complain to your data protection authority.',
        },
        s7: {
          heading: 'Children',
          body: 'We ask your age group to decide which chat is available. Players under 14 get quick phrases only. [Parental consent rules by country: to be decided with a lawyer.]',
        },
        s8: {
          heading: 'Cookies and storage',
          body: 'The game stores your settings, progress and sign-in session in your browser or app. These are needed for the game to work. We do not use advertising cookies.',
        },
      },
    },
    deleteAccount: {
      title: 'Delete your account',
      intro:
        'You can delete your Il Colpo account and the data we keep about it at any time, from the game itself.',
      steps: {
        s1: 'Open Il Colpo and go to Account.',
        s2: 'Sign in, then open Privacy and data.',
        s3: 'Choose Delete my account, and pick whether to delete in seven days or right now.',
      },
      what: 'What is erased: your profile, your consents and your cloud save. What stays: the progress saved on your own device, until you remove it from the game.',
      open: 'Open Privacy and data',
      contact:
        '[Support contact for people who can no longer sign in: to be filled in by the developer.]',
    },
  },
};
